import { useCallback, useEffect, useRef } from 'react';
import { getApiUrl } from '@/config/api';
import { safeJsonParse } from '@/utils/apiUtils';
import { useChatSessionContext, ChatMessage } from '@/contexts/ChatSessionContext';

export interface Attachment {
  chart_id: string;
  title: string;
  period: { start: string; end: string };
  athletes: string[];
  data: { kind: string; rows?: unknown[]; series?: unknown[] };
}

interface UseChatSessionOptions {
  teamId: number;
  days: number;
}

/**
 * Shared chat hook used by BOTH the /chat tab and the analytics drawer.
 * Owns the send/fetch logic; state lives in ChatSessionContext so the
 * two surfaces see the same conversation.
 */
export function useChatSession({ teamId, days }: UseChatSessionOptions) {
  const {
    sessionId, setSessionId,
    messages, setMessages,
    isSending, setIsSending,
  } = useChatSessionContext();

  // Clear session + message state when the coach switches teams. Without this,
  // the drawer would keep showing the prior team's history and the next send
  // would post { team_id: <new>, session_id: <stale from old team> }. The
  // backend would silently create a new session (team mismatch on the stored
  // session), but the UI would stay desynced until a manual "New chat" or
  // reload. A ref guards against clearing on the very first mount (where the
  // previous teamId is undefined, not "different").
  const prevTeamIdRef = useRef<number | null>(null);
  useEffect(() => {
    if (prevTeamIdRef.current !== null && prevTeamIdRef.current !== teamId) {
      setSessionId(null);
      setMessages([]);
    }
    prevTeamIdRef.current = teamId;
  }, [teamId, setSessionId, setMessages]);

  // Hydrate session history when sessionId changes and we haven't loaded messages yet.
  useEffect(() => {
    if (!sessionId || messages.length > 0) return;
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('access');
        const resp = await fetch(getApiUrl(`/api/ai/chat/sessions/${sessionId}/`), {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        const result = await safeJsonParse<{ messages: ChatMessage[] }>(
          resp, 'fetching chat history'
        );
        if (cancelled) return;
        if (result.success && result.data?.messages) {
          // Functional setState so the "is messages still empty?" check
          // reads the latest state at application time rather than the
          // stale closure value. If a concurrent send added an optimistic
          // message while the fetch was in flight, skip the hydration
          // overwrite so we don't clobber it.
          const hydrated = result.data.messages;
          setMessages(prev => prev.length > 0 ? prev : hydrated);
        }
      } catch (err) {
        console.error('History hydration failed:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [sessionId, messages.length, setMessages]);

  const send = useCallback(
    async (message: string, attachments: Attachment[]) => {
      if (!message.trim() || isSending) return;

      const userMsg: ChatMessage = {
        id: `tmp_${Date.now()}`,
        role: 'user',
        content: message,
        created_at: new Date().toISOString(),
        attachments,
      };
      setMessages(prev => [...prev, userMsg]);
      setIsSending(true);

      try {
        const token = localStorage.getItem('access');
        const response = await fetch(getApiUrl('/api/ai/chat/'), {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            message,
            team_id: teamId,
            days,
            session_id: sessionId,
            attachments,
          }),
        });

        const result = await safeJsonParse<{
          success?: boolean;
          response: string;
          session_id?: string;
          user_message_id?: string;
          assistant_message_id?: string;
          retrieved_chunks?: Array<{ document_title: string; similarity: number }>;
          error?: string;
        }>(response, 'sending chat message');

        if (!result.success) {
          throw new Error(result.error || 'Failed to get AI response');
        }
        if (result.data!.success === false) {
          throw new Error(result.data!.error || 'AI service returned an error');
        }

        if (result.data!.session_id && !sessionId) {
          setSessionId(result.data!.session_id);
        }

        const assistantMsg: ChatMessage = {
          id: result.data!.assistant_message_id ?? `tmp_a_${Date.now()}`,
          role: 'assistant',
          content: result.data!.response,
          created_at: new Date().toISOString(),
          retrieved_chunks: result.data!.retrieved_chunks,
        };
        // Reconcile the optimistic user message id with the server-assigned
        // user_message_id so subsequent history merges don't duplicate it.
        setMessages(prev => {
          const withCanonicalUser = prev.map(m =>
            m.id === userMsg.id
              ? { ...m, id: result.data!.user_message_id ?? m.id }
              : m
          );
          return [...withCanonicalUser, assistantMsg];
        });
      } catch (err) {
        console.error('Chat error:', err);
        const errorMsg: ChatMessage = {
          id: `tmp_e_${Date.now()}`,
          role: 'assistant',
          content: `Sorry, I encountered an error: ${
            err instanceof Error ? err.message : 'Failed to get response'
          }\n\nPlease try again.`,
          created_at: new Date().toISOString(),
        };
        setMessages(prev => [...prev, errorMsg]);
      } finally {
        setIsSending(false);
      }
    },
    [teamId, days, sessionId, isSending, setMessages, setSessionId, setIsSending]
  );

  const startNewSession = useCallback(() => {
    setSessionId(null);
    setMessages([]);
  }, [setSessionId, setMessages]);

  /**
   * Jump to a prior conversation. Clears the visible message list so the
   * hydration effect refetches history for the new session id. Without
   * the clear, the guard in the hydrate effect (`messages.length > 0`)
   * would keep the current conversation's messages on screen.
   */
  const switchToSession = useCallback((id: string) => {
    setMessages([]);
    setSessionId(id);
  }, [setSessionId, setMessages]);

  return { sessionId, messages, isSending, send, startNewSession, switchToSession };
}
